import type { ReactElement } from 'react'
import { useCallback, useState } from 'react'
import { Button, CircularProgress, Typography } from '@mui/material'
import { useCurrentChain } from '@/hooks/useChains'
import useOnboard from '@/hooks/wallets/useOnboard'
import useIsWrongChain from '@/hooks/useIsWrongChain'
import { switchWalletChain } from '@/services/tx/tx-sender/sdk'
import { useAppSelector } from '@/store'
import { selectEnvironment } from '@/store/hederaSettingsSlice'
import { switchHederaChain } from '@/hooks/wallets/useHashPack'

const ChainSwitcher = ({
  fullWidth,
  primaryCta = false,
}: {
  fullWidth?: boolean
  primaryCta?: boolean
}): ReactElement | null => {
  const chain = useCurrentChain()
  const onboard = useOnboard()
  const isWrongChain = useIsWrongChain()
  const [loading, setIsLoading] = useState<boolean>(false)
  const environment = useAppSelector(selectEnvironment)

  const handleChainSwitch = useCallback(async () => {
    if (!chain) return

    setIsLoading(true)
    try {
      if (environment === 'hedera') {
        await switchHederaChain(chain.chainId)
        return
      }

      if (!onboard) return
      await switchWalletChain(onboard, chain.chainId)
    } finally {
      setIsLoading(false)
    }
  }, [chain, onboard, environment])

  if (!isWrongChain) return null

  return (
    <Button
      onClick={handleChainSwitch}
      variant={primaryCta ? 'contained' : 'outlined'}
      sx={{ minWidth: '200px' }}
      size={primaryCta ? 'medium' : 'small'}
      fullWidth={fullWidth}
      color="primary"
      disabled={loading}
    >
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Typography noWrap>Switch to&nbsp;</Typography>
          <img
            src={chain?.chainLogoUri ?? undefined}
            alt={`${chain?.chainName} Logo`}
            width={24}
            height={24}
            loading="lazy"
          />
          <Typography noWrap>&nbsp;{chain?.chainName}</Typography>
        </>
      )}
    </Button>
  )
}

export default ChainSwitcher
